<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  ticketsPerBatch: {
    type: Number,
    default: 100,
  },
  initialSelectedTickets: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['selected-tickets'])

const selectedTickets = ref([])

const normalizeSelection = (tickets) => {
  if (!Array.isArray(tickets)) return []
  const max = Number(props.ticketsPerBatch) || 0
  const values = tickets
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 1 && (max <= 0 || value <= max))
  return [...new Set(values)].sort((a, b) => a - b)
}

watch(
  () => props.initialSelectedTickets,
  (tickets) => {
    selectedTickets.value = normalizeSelection(tickets)
  },
  { immediate: true, deep: true },
)

const columns = computed(() => 10)
const gridItems = computed(() => {
  return Array.from({ length: props.ticketsPerBatch }, (_, i) => i + 1)
})

const emitSelection = () => {
  emit('selected-tickets', [...selectedTickets.value])
}

const toggleTicket = (num) => {
  const idx = selectedTickets.value.indexOf(num)
  if (idx >= 0) {
    selectedTickets.value.splice(idx, 1)
  } else {
    selectedTickets.value.push(num)
  }
  selectedTickets.value.sort((a, b) => a - b)
  emitSelection()
}

const selectAllTickets = () => {
  selectedTickets.value = [...gridItems.value]
  emitSelection()
}

const clearAllTickets = () => {
  selectedTickets.value = []
  emitSelection()
}

const isSelected = (num) => selectedTickets.value.includes(num)

defineExpose({
  selectedTickets,
})
</script>

<template>
  <div class="ticket-grid-wrapper">
    <div class="info">
      <p>Selected: <strong>{{ selectedTickets.length }} / {{ ticketsPerBatch }}</strong></p>
    </div>

    <div class="bulk-actions action-bar sticky-action-bar">
      <button type="button" class="btn btn-secondary" :disabled="selectedTickets.length === ticketsPerBatch" @click="selectAllTickets">
        Select all
      </button>
      <button type="button" class="btn btn-secondary" :disabled="selectedTickets.length === 0" @click="clearAllTickets">
        Clear all
      </button>
      <span class="bulk-selected-count">{{ selectedTickets.length }} / {{ ticketsPerBatch }} selected</span>
    </div>

    <div class="grid" :style="{ gridTemplateColumns: `repeat(${columns}, 1fr)` }">
      <button
        v-for="ticket in gridItems"
        :key="ticket"
        class="ticket-cell"
        :class="{ selected: isSelected(ticket) }"
        @click="toggleTicket(ticket)"
      >
        {{ ticket }}
      </button>
    </div>

    <div v-if="selectedTickets.length > 0" class="selected-list">
      <p><strong>Selected tickets:</strong></p>
      <p class="tickets-display">{{ selectedTickets.join(', ') }}</p>
    </div>
  </div>
</template>

<style scoped>
.ticket-grid-wrapper {
  display: grid;
  gap: 1.5rem;
}

.info {
  padding: 1rem;
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
  border-radius: 4px;
  color: #1976d2;
}

.grid {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.bulk-actions {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem;
}

.bulk-selected-count {
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: auto;
}

.ticket-cell {
  padding: 1rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
  color: #111827;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ticket-cell:hover {
  border-color: #2196f3;
  background-color: #e3f2fd;
}

.ticket-cell.selected {
  background-color: #2196f3;
  color: white;
  border-color: #1976d2;
}

.selected-list{
  padding: 1rem;
  background-color: #f0f4ff;
  border-radius: 4px;
  border-left: 4px solid #2196f3;
}

.tickets-display {
  word-break: break-word;
  color: #111827;
  font-family: monospace;
  font-size: 0.85rem;
}

@media (max-width: 768px) {
  .bulk-actions {
    position: sticky;
    top: 56px;
    z-index: 20;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }
}
</style>

